// The instrument register: what measures product, and whether it can still be
// believed today.
//
// CALIBRATION IS A GATE, NOT A REMINDER. A parameter that requires an instrument
// is refused at ingest when that instrument is out of calibration or out of
// service. So "overdue" here is not a nag — it is the state in which a check
// physically cannot be captured, and it carries the OUT colour for the same reason
// an out-of-spec reading does: product measured on it is in question.
//
// AS-FOUND IS THE COLUMN THAT MATTERS. As-left says the instrument is fine now.
// As-found is the only thing that answers "which product was made since the last
// good calibration" — and a calibration record without it cannot answer that
// question later, at the point somebody is asking it under pressure. The form
// below will not submit without it.
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { api } from '../../lib/api';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/DataTable';
import { Saved } from '@/components/Saved';
import {
  grantHint,
  localDate,
  localInstant,
  officeFocus,
  rows,
  serverMessage,
  useMyGrants,
  useResource,
} from '../../shells/OfficeShell';

/** GET /meters — quality_meter_status, an ENVELOPE with the rows under `meters`.
 *  `calibrationOverdue`, `blocksCapture` and `blockReason` are the SERVER's
 *  answers: whether an instrument is overdue right now is derived once, in the
 *  tool the ingest gate shares, so this page does not re-derive arithmetic it
 *  could get the sign wrong on. */
interface MetersPayload {
  meters?: Meter[];
}

interface Meter {
  id: number;
  code: string;
  name: string;
  kind: string;
  calibrationRequired: boolean;
  calibrationDueAt: string | null;
  /** Fractional days, negative once the due date has passed. */
  daysUntilDue: number | null;
  calibrationOverdue: boolean;
  blocksCapture: boolean;
  blockReason: string | null;
  status: 'IN_SERVICE' | 'OUT_OF_SERVICE';
}

interface CalibrationState {
  label: string;
  /** Tailwind colour class. Red is used here in the same sense as an OUT verdict. */
  tone: string;
}

function calibrationState(m: Meter): CalibrationState {
  if (m.status === 'OUT_OF_SERVICE') {
    return { label: 'Out of service', tone: 'text-(--bad) font-bold' };
  }
  // Read from CalibrationRequired, never inferred from a missing due date. A null
  // date can mean "never calibrated", "not yet scheduled" or "exempt", and only
  // the flag distinguishes exempt from the two that should be refusing captures.
  if (!m.calibrationRequired) {
    return { label: 'Calibration not required', tone: 'text-(--muted)' };
  }
  const d = m.daysUntilDue === null ? null : Math.round(m.daysUntilDue);
  if (m.calibrationOverdue) {
    return {
      label: d === null ? 'Overdue — no due date' : `Overdue by ${Math.abs(d)} d`,
      tone: 'text-(--bad) font-bold',
    };
  }
  if (d === null) return { label: 'No due date', tone: 'text-(--bad) font-bold' };
  if (d <= 7) return { label: `Due in ${d} d`, tone: 'text-(--warn) font-semibold' };
  return { label: `Due in ${d} d`, tone: 'text-(--muted)' };
}

function day(iso: string | null): string {
  if (!iso) return '—';
  return localDate(iso) ?? iso;
}

const col = createColumnHelper<Meter>();

/** Sorting on Calibration puts the most urgent first: out of service, then
 *  overdue or undated, then by days left; exempt instruments last. */
function urgency(m: Meter): number {
  if (m.status === 'OUT_OF_SERVICE') return -2e9;
  if (!m.calibrationRequired) return 2e9;
  if (m.calibrationOverdue || m.daysUntilDue === null) return -1e9 + (m.daysUntilDue ?? 0);
  return m.daysUntilDue;
}

const FIELD =
  'flex min-h-[44px] w-full rounded-md border border-hairline-strong bg-card px-3 text-sm ' +
  'transition-[border-color,box-shadow] duration-150 motion-reduce:transition-none';
const LABEL =
  'block text-[13px] font-semibold text-muted-foreground';

export function Meters() {
  const meters = useResource<MetersPayload>('/meters');
  const grants = useMyGrants();

  const [selected, setSelected] = useState<Meter | null>(null);
  const [asFound, setAsFound] = useState('');
  const [asLeft, setAsLeft] = useState('');
  const [standard, setStandard] = useState('');
  const [nextDueAt, setNextDueAt] = useState('');
  // Neither answer is chosen until the person chooses (UI/UX audit, 31 Oct 2026, UX-01): the form
  // opened with Passed ticked, and a pass with a next-due date moves CalibrationDueAt forward - the
  // gate itself - on one click nobody made.
  const [passed, setPassed] = useState<boolean | null>(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  // What the last calibration recorded (audit finding 15): shown where the form was, until the
  // next instrument is chosen or the next submit.
  const [saved, setSaved] = useState<string | null>(null);

  const hint = grantHint(grants, 'METER_ADMIN', 'Recording a calibration');
  const canRecord = hint === null;
  const outcomeHint = passed === null ? 'Choose Passed or Failed.' : null;

  // Focus (audit finding 19): the form opens on As found; when it closes after a record, focus
  // goes back to the row button that opened it (looked up by id, since the register re-renders).
  const asFoundInput = useRef<HTMLInputElement>(null);
  const returnTo = useRef<string | null>(null);
  useEffect(() => {
    if (selected) asFoundInput.current?.focus();
    else if (returnTo.current) {
      document.getElementById(returnTo.current)?.focus();
      returnTo.current = null;
    }
  }, [selected]);

  const choose = useCallback((m: Meter) => {
    setSelected(m);
    returnTo.current = `record-calibration-${m.id}`;
    setAsFound('');
    setAsLeft('');
    setStandard('');
    setNextDueAt('');
    setPassed(null);
    setNotes('');
    setRefusal(null);
    setSaved(null);
  }, []);

  const columns = useMemo(
    () => [
      col.accessor('code', { header: 'Code', meta: { rowHeader: true, mono: true, nowrap: true } }),
      col.accessor('name', { header: 'Instrument' }),
      col.accessor('kind', { header: 'Kind' }),
      col.accessor((m) => calibrationState(m).label, {
        id: 'calibration',
        header: 'Calibration',
        sortingFn: (a, b) => urgency(a.original) - urgency(b.original),
        cell: (c) => {
          const state = calibrationState(c.row.original);
          return <span className={state.tone}>{state.label}</span>;
        },
      }),
      col.accessor((m) => m.calibrationDueAt ?? '', {
        id: 'due',
        header: 'Due',
        cell: (c) => day(c.row.original.calibrationDueAt),
        meta: { mono: true, nowrap: true },
      }),
      col.accessor((m) => `${m.status === 'IN_SERVICE' ? 'In service' : 'Out of service'} ${m.blockReason ?? ''}`, {
        id: 'status',
        header: 'Status',
        cell: (c) => {
          const m = c.row.original;
          return (
            <>
              {m.status === 'IN_SERVICE' ? 'In service' : 'Out of service'}
              {/* The server's sentence for why capture is refused on this
                  instrument, shown where somebody is deciding what to do
                  about it rather than left for them to infer. */}
              {m.blocksCapture && m.blockReason ? (
                <span className="block font-semibold text-(--bad)">{m.blockReason}</span>
              ) : null}
            </>
          );
        },
      }),
      col.display({
        id: 'record',
        header: () => <span className="sr-only">Record calibration</span>,
        cell: (c) => (
          <Button
            variant="outline"
            type="button"
            id={`record-calibration-${c.row.original.id}`}
            onClick={() => choose(c.row.original)}
          >
            Record calibration
          </Button>
        ),
      }),
    ],
    [choose],
  );

  async function record(e: FormEvent) {
    e.preventDefault();
    if (!selected || !canRecord || passed === null) return;
    // Checked here as well as by `required` on the input: `Number('')` is 0, and a
    // blank as-found posted as a confident zero is a calibration record that
    // looks complete and cannot bound the product at risk.
    const found = Number(asFound);
    if (asFound.trim() === '' || !Number.isFinite(found)) {
      setRefusal('As found is required — it is what bounds the product measured since the last good calibration.');
      return;
    }

    // ONLY A PASS WITH A NEXT-DUE DATE MOVES THE GATE. The API advances
    // Meter.CalibrationDueAt on exactly that combination, so a passed calibration
    // saved without a date is recorded and changes nothing: the instrument stays
    // overdue and goes on refusing every reading, with a green calibration record
    // sitting behind it.
    const nextDueInstant = nextDueAt === '' ? null : localInstant(nextDueAt);
    if (passed && !nextDueInstant) {
      setRefusal(
        'A passed calibration needs a next-due date. Without one the instrument stays overdue and ' +
          'goes on refusing readings, however good this calibration was.',
      );
      return;
    }

    setSaving(true);
    setRefusal(null);
    setSaved(null);
    try {
      await api.post(`/meters/${selected.id}/events`, {
        eventType: 'CALIBRATED',
        asFound: found,
        asLeft: asLeft === '' ? null : Number(asLeft),
        standard: standard === '' ? null : Number(standard),
        passed,
        nextDueAt: nextDueInstant,
        notes: notes.trim() || null,
      });
      setSaved(
        `Calibration recorded for ${selected.code} · ${selected.name}: as found ${found}, ${
          passed ? `passed, next due ${day(nextDueInstant)}` : 'failed'
        }.`,
      );
      setSelected(null);
      meters.reload();
    } catch (err: unknown) {
      setRefusal(serverMessage(err));
    } finally {
      setSaving(false);
    }
  }

  const list = rows<Meter>(meters.data?.meters);

  return (
    <div className="flex flex-col gap-(--s5)">
      <header>
        <h1 className="m-0 font-display text-[34px] font-semibold leading-none tracking-[-0.01em]">Meters</h1>
        <p className="m-0 mt-(--s1) text-(length:--fs-sm) text-(--muted)">
          Instruments and their calibration. Readings on one out of calibration are refused.
        </p>
      </header>


      <div className="grid grid-cols-1 gap-(--s5) xl:grid-cols-[minmax(0,1fr)_360px]">
        {/* The red bar is the server's own answer, not a second opinion: the
            same flag the capture gate refuses on, so a marked row is exactly a
            row whose readings are being refused. */}
        <DataTable
          data={list}
          columns={columns}
          caption="Instruments and their calibration state"
          getRowId={(m) => String(m.id)}
          rowAccent={(m) => (m.blocksCapture ? 'bad' : null)}
          search="Search code, instrument or kind"
          loading={meters.loading}
          error={meters.error}
          onRetry={meters.reload}
          // Said beside the header's "Readings on one out of calibration are refused" (UI/UX audit,
          // 31 Oct 2026, UX-51): with no instrument registered that sentence refuses nothing, and a
          // reader should not go looking for the calibration that is stopping them. The gate acts
          // only where a spec version requires an instrument; the order is pilot runbook step 4's.
          empty={
            <>
              No instruments registered, so calibration refuses no reading yet: it acts only on a parameter
              whose spec requires an instrument. Register the instrument first, then approve a spec version
              that requires it (pilot runbook, step 4).
            </>
          }
        />

        <section className="rounded-(--radius) border border-(--border) bg-(--card) shadow-card p-(--s4)">
          <h2 className="m-0 font-display text-[22px] font-semibold leading-tight">Record a calibration</h2>
          {!selected ? (
            <div className="mt-(--s2) flex flex-col gap-(--s2)">
              {saved ? <Saved>{saved}</Saved> : null}
              <p className="m-0 text-(length:--fs-sm) text-(--muted)">
                Choose an instrument from the register.
              </p>
            </div>
          ) : (
            <form onSubmit={record} className="mt-(--s3) flex flex-col gap-(--s3)">
              <p className="m-0 font-mono text-(length:--fs-sm) font-semibold">
                {selected.code} · {selected.name}
              </p>

              <div>
                <label htmlFor="as-found" className={LABEL}>
                  As found (required)
                </label>
                {/* Required, and the only required number on this form. Without it
                    the record cannot say whether the instrument had been reading
                    wrong, and therefore cannot bound which product is affected. */}
                <input
                  id="as-found"
                  ref={asFoundInput}
                  type="number"
                  step="any"
                  required
                  value={asFound}
                  onChange={(e) => setAsFound(e.target.value)}
                  className={`${FIELD} ${officeFocus}`}
                />
                <p className="m-0 mt-(--s1) text-(length:--fs-xs) text-(--muted)">
                  What it read before adjustment — this is what bounds the product at risk.
                </p>
              </div>

              <div>
                <label htmlFor="as-left" className={LABEL}>
                  As left
                </label>
                <input
                  id="as-left"
                  type="number"
                  step="any"
                  value={asLeft}
                  onChange={(e) => setAsLeft(e.target.value)}
                  className={`${FIELD} ${officeFocus}`}
                />
              </div>

              <div>
                <label htmlFor="standard" className={LABEL}>
                  Reference standard
                </label>
                <input
                  id="standard"
                  type="number"
                  step="any"
                  value={standard}
                  onChange={(e) => setStandard(e.target.value)}
                  className={`${FIELD} ${officeFocus}`}
                />
              </div>

              <div>
                <label htmlFor="next-due" className={LABEL}>
                  Next due {passed === true ? '(required)' : ''}
                </label>
                <input
                  id="next-due"
                  type="date"
                  required={passed === true}
                  value={nextDueAt}
                  onChange={(e) => setNextDueAt(e.target.value)}
                  className={`${FIELD} ${officeFocus}`}
                />
                <p className="m-0 mt-(--s1) text-(length:--fs-xs) text-(--muted)">
                  This date is the gate. Until it passes, checks bound to this instrument are
                  accepted; after it, they are refused.
                </p>
              </div>

              {/* Two answers, neither chosen (UX-01): a checkbox cannot say "not
                  answered yet", so its unticked state read as Failed. Each label
                  wraps its control, so the 44px target is the whole row. */}
              <fieldset className="m-0 flex flex-wrap gap-(--s3) border-0 p-0">
                <legend className={LABEL}>Outcome (required)</legend>
                {([true, false] as const).map((opt) => (
                  <label
                    key={String(opt)}
                    className="flex min-h-[44px] cursor-pointer items-center gap-(--s2) text-(length:--fs-sm) font-semibold"
                  >
                    <input
                      type="radio"
                      name="calibration-outcome"
                      checked={passed === opt}
                      onChange={() => setPassed(opt)}
                      className={`h-[24px] w-[24px] ${officeFocus}`}
                    />
                    {opt ? 'Passed' : 'Failed'}
                  </label>
                ))}
              </fieldset>

              <div>
                <label htmlFor="cal-notes" className={LABEL}>
                  Notes
                </label>
                <input
                  id="cal-notes"
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className={`${FIELD} ${officeFocus}`}
                />
              </div>

              <span title={hint ?? outcomeHint ?? 'Record this calibration'}>
                <Button variant="default" className="w-full"
                  type="submit"
                  disabled={!canRecord || saving || passed === null}
                  aria-describedby="meter-hint"
                >
                  {saving ? 'Recording…' : 'Record calibration'}
                </Button>
              </span>
              <p
                id="meter-hint"
                aria-live="polite"
                className="m-0 text-(length:--fs-xs) text-(--muted)"
              >
                {hint ?? outcomeHint ?? 'Recorded against your account, with the date it was taken.'}
              </p>

              {refusal ? (
                <p role="alert" className="m-0 text-(length:--fs-sm) font-semibold text-(--warn)">
                  {refusal}
                </p>
              ) : null}
            </form>
          )}
        </section>
      </div>
    </div>
  );
}

export default Meters;
